/**
 * The Status column's cell, shared by both document grids — the client-side
 * `DocumentBrowser` and the server-side `DocumentBrowserContainer`.
 *
 * One column carries both readings of "status": a quarantined document reports
 * what it needs ("Needs review"), and anything else reports how it is shared
 * (Internal / External). The two grids MUST render that identically — they sit
 * behind the same tab bar, and a user switching tabs is looking at one table as
 * far as they know. It lived twice, once per grid, which is how the colours and
 * the row tint came to be duplicated literals.
 */
import React from "react";
import { Icon } from "@jsq/ds/icons";
import * as styles from "./styles_vanilla.css";

/** Status shown for a quarantined document — it needs a steward's attention. */
export const NEEDS_REVIEW = "Needs review";

/**
 * Row tint for a document that needs review. Kept next to the cell that names the
 * state so the two can't drift apart.
 */
export const REVIEW_ROW_STYLE = { background: "rgba(180, 120, 20, 0.05)" };

/* Icon colours match the text colour each `styles.status` variant sets: the DS
   `Icon` takes a colour prop rather than inheriting `currentColor`. */
const REVIEW_COLOR = "#c8421e"; // warning-600
const EXTERNAL_COLOR = "#00614e"; // success-600
const INTERNAL_COLOR = "#504f4c"; // neutral-700

export function StatusCell({ value }: { value?: unknown }) {
  if (value === NEEDS_REVIEW)
    return (
      <span className={styles.status.review}>
        <Icon type="exclamation-triangle" size="small" customColor={REVIEW_COLOR} />
        {NEEDS_REVIEW}
      </span>
    );
  if (value === "External")
    return (
      <span className={styles.status.external}>
        <Icon type="share" size="small" customColor={EXTERNAL_COLOR} />
        External
      </span>
    );
  if (value === "Internal")
    return (
      <span className={styles.status.internal}>
        <Icon type="lock" size="small" customColor={INTERNAL_COLOR} />
        Internal
      </span>
    );
  return <span className={styles.dash}>—</span>;
}
